import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Table,
  TableProps,
  Container,
  PropertyFilter,
} from "@cloudscape-design/components";
import { filterWorkflowsByTag } from "../../utils/workflowHelpers";
import { sanitizeForDisplay, validateRequiredIds } from "../../utils/security";
import { Button } from "../common/Button";
import { TrajectoryLoader } from "../common/Loaders";
import { ConfirmationModal, RunWorkflowModal } from "../common/Modals";
import { Workflow } from "@amzn/finspace-service-typescript-client";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { deriveCategory } from "../../utils/activityTransformUtils";
import { useTableSorting, createStandardComparator, usePropertyFilter } from "../../hooks";
import { formatTimestamp } from "../../utils/dateTimeUtils";
import { PROPERTY_FILTER_I18N_STRINGS } from "../../constants/cloudscape-i18n";
import { getWorkflowDisplayName } from "../../constants/workflow-tags";
import "./workflows-page.scss";
import {ROUTES} from "src/constants";
import { logger } from '../../utils/logger';

// Extend Workflow type to include createdTime and lastUpdatedTime properties
type WorkflowWithTime = Workflow & { createdTime?: string; lastUpdatedTime?: string };

interface WorkflowsPageProps {
  tag?: string;
}

const WorkflowsPage: React.FC<WorkflowsPageProps> = ({ tag }) => {
  const navigate = useNavigate();
  const { selectedSpaceId, fetchSpaces } = useGlobalStore();
  const { workflows, isLoading, isExecuting, isDeleting, fetchWorkflows, executeWorkflow, deleteWorkflow } = useWorkflowStore();
  
  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    workflow: Workflow | null;
  }>({ visible: false, workflow: null });
  
  const [runModal, setRunModal] = useState<{
    visible: boolean;
    workflow: Workflow | null;
  }>({ visible: false, workflow: null });
  
  // Fetch spaces and workflows on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflows]);
  
  const tagFilteredWorkflows = filterWorkflowsByTag(workflows, tag);

  // Property filter for name and description
  const {
    query: filteringQuery,
    setQuery: setFilteringQuery,
    filteredItems: filteredWorkflows,
    filteringProperties,
    filteringOptions,
  } = usePropertyFilter({
    items: tagFilteredWorkflows,
    filterConfigs: [
      {
        key: 'name',
        propertyLabel: 'Workflow Name',
        groupValuesLabel: 'Workflow Name values',
        getValue: (workflow: Workflow) => workflow.name,
      },
      {
        key: 'description',
        propertyLabel: 'Description',
        groupValuesLabel: 'Description values',
        getValue: (workflow: Workflow) => workflow.description,
      },
    ],
  });

  const sortingComparator = createStandardComparator<Workflow>({
    name: (item) => item.name?.toLowerCase() || "",
    createdDate: (item) => {
      const workflow = item as WorkflowWithTime;
      return workflow.createdTime ? new Date(workflow.createdTime).getTime() : 0;
    },
    updatedDate: (item) => {
      const workflow = item as WorkflowWithTime;
      return workflow.lastUpdatedTime ? new Date(workflow.lastUpdatedTime).getTime() : 0;
    },
  });

  const {
    sortedItems: sortedWorkflows,
    sortingColumn,
    sortingDescending,
    handleSortingChange,
  } = useTableSorting({
    items: filteredWorkflows,
    defaultSortingField: "createdDate",
    defaultSortingDescending: true,
    sortingComparator,
  });

  const pageTitle = tag 
    ? `${getWorkflowDisplayName(tag)} Workflows` 
    : 'All Workflows';

  const getCreateRoute = () => {
    switch (tag) {
      case 'MFR':
        return ROUTES.MFR_CREATE;
      case 'PPM':
        return ROUTES.NETPPM_CREATE;
      case 'RVM':
        return ROUTES.RVM_CREATE;
      default:
        return null;
    }
  };

  const handleRun = async (workflow: Workflow) => {
    setRunModal({ visible: true, workflow });
  };

  const handleRunConfirm = async (input: any, presentationContext?: any) => {
    const { workflow } = runModal;
    if (!workflow || !validateRequiredIds(selectedSpaceId, workflow.id)) {
      logger.error('Missing required IDs for workflow execution');
      setRunModal({ visible: false, workflow: null });
      return;
    }
    
    const isMFRWorkflow = workflow.tags?.includes('MFR');
    const category = deriveCategory(workflow.tags)?.toLowerCase() || 'mfr';
    
    try {
      const executionResponse = await executeWorkflow({
        spaceId: selectedSpaceId!,
        workflowId: workflow.id!,
        input: input,
        presentationContext: presentationContext
      });
      
      // Navigate to the run page with execution details
      navigate(`/reports-and-analysis/space/${selectedSpaceId}/${category}/${workflow.id}/run/${executionResponse?.workflowExecutionId}`, { 
        state: { 
          selectedPeriod: isMFRWorkflow && Array.isArray(input.period) && input.period.length > 0 ? input.period[0] : undefined,
          workflowExecutionId: executionResponse?.workflowExecutionId,
          spaceId: selectedSpaceId
        } 
      });
      setRunModal({ visible: false, workflow: null });
    } catch (error) {
      logger.error('Failed to execute workflow:', error as Error);
    }
  };

  const handleRunCancel = () => {
    setRunModal({ visible: false, workflow: null });
  };

  const getEditUrl = (workflow: Workflow): string | undefined => {
    const category = deriveCategory(workflow.tags)?.toLowerCase();
    
    if (category && workflow.id) {
      return `/reports-and-analysis/${category}/edit/${workflow.id}`;
    }
    return undefined;
  };

  const handleDeleteClick = (workflow: Workflow) => {
    setDeleteModal({ visible: true, workflow });
  };

  const handleDeleteConfirm = async () => {
    const { workflow } = deleteModal;
    if (!workflow || !validateRequiredIds(selectedSpaceId, workflow.id)) {
      logger.error('Missing required IDs for workflow deletion');
      setDeleteModal({ visible: false, workflow: null });
      return;
    }

    try {
      await deleteWorkflow(selectedSpaceId!, workflow.id!);
      setDeleteModal({ visible: false, workflow: null });
    } catch (error) {
      logger.error('Failed to delete workflow:', error as Error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ visible: false, workflow: null });
  };

  const handleBack = () => {
    navigate(ROUTES.DASHBOARD);
  };

  const columnDefinitions: TableProps.ColumnDefinition<Workflow>[] = [
    {
      id: "workflow",
      header: <Box fontWeight="bold">Workflow Name</Box>,
      cell: (workflow: Workflow) => (
        <Box>
          <div className="workflow-name">
            {workflow.name}
          </div>
          {workflow.description && (
            <div className="workflow-description">
              {workflow.description}
            </div>
          )}
        </Box>
      ),
      sortingField: "name",
      width: "39%",
      minWidth: "280px",
    },
    {
      id: "createdDate",
      header: <Box fontWeight="bold">Created At</Box>,
      cell: (workflow: Workflow) => {
        const workflowWithTime = workflow as WorkflowWithTime;
        return formatTimestamp(workflowWithTime.createdTime);
      },
      sortingField: "createdDate",
      width: "18%",
      minWidth: "180px",
    },
    {
      id: "updatedDate",
      header: <Box fontWeight="bold">Last Updated At</Box>,
      cell: (workflow: Workflow) => {
        const workflowWithTime = workflow as WorkflowWithTime;
        return formatTimestamp(workflowWithTime.lastUpdatedTime);
      },
      sortingField: "updatedDate",
      width: "18%",
      minWidth: "180px",
    },
    {
      id: "actions",
      header: <Box fontWeight="bold">Actions</Box>,
      cell: (workflow: Workflow) => (
        <div className="workflow-actions">
          <Button
            variant="primary"
            icon="play"
            onClick={() => handleRun(workflow)}
            loading={isExecuting}
          >
            Run
          </Button>
          
          <Button
            variant="secondary"
            icon="edit"
            href={getEditUrl(workflow)}
            disabled={!getEditUrl(workflow)}
          >
            Edit
          </Button>
          
          <Button
            variant="secondary"
            icon="remove"
            onClick={() => handleDeleteClick(workflow)}
            loading={isDeleting}
          >
            Delete
          </Button>
        </div>
      ),
      width: "25%",
      minWidth: "250px",
    },
  ];

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state-loader">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">
            Loading workflows...
          </div>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="secondary"
                icon="arrow-left"
                onClick={handleBack}
              >
                Back
              </Button>
              {getCreateRoute() && (
                <Button
                  variant="primary"
                  icon="add-plus"
                  href={getCreateRoute()!}
                >
                  Create
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {pageTitle}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Container
          header={
            <PropertyFilter
              query={filteringQuery}
              onChange={({ detail }) => setFilteringQuery(detail)}
              filteringProperties={filteringProperties}
              filteringOptions={filteringOptions}
              countText={`${sortedWorkflows.length} ${sortedWorkflows.length === 1 ? 'match' : 'matches'}`}
              i18nStrings={{
                ...PROPERTY_FILTER_I18N_STRINGS,
                filteringPlaceholder: "Search by workflow name or description",
              }}
            />
          }
        >
          <Table
            columnDefinitions={columnDefinitions}
            items={sortedWorkflows}
            loading={isLoading}
            loadingText="Loading workflows..."
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No workflows found</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                  {tag
                    ? `No workflows have been created from ${tag} template yet.`
                    : 'No workflows have been created yet.'
                  }
                </Box>
              </Box>
            }
            variant="borderless"
          />
        </Container>
      </SpaceBetween>

      <ConfirmationModal
        visible={deleteModal.visible}
        title="Delete Workflow"
        message={`Are you sure you want to delete workflow "${sanitizeForDisplay(deleteModal.workflow?.name)}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="primary"
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <RunWorkflowModal
        visible={runModal.visible}
        workflow={runModal.workflow}
        loading={isExecuting}
        onRun={handleRunConfirm}
        onCancel={handleRunCancel}
      />
    </ContentLayout>
  );
};

export default WorkflowsPage;
